import 'react-datepicker/dist/react-datepicker.css'
import './MonthPicker.css'
import { ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { ComponentPropsWithoutRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import ReactDatePicker from 'react-datepicker'

import { cn } from '@sparkai/utils'

import { MonthPickerHeader } from './MonthPickerHeader'
import { getFullMonth, getLastOneMonth } from './monthPickerUtils'

export interface DateInterval {
  start: Date
  end: Date
}

export interface DatePeriod {
  start: string
  end: string
}

export interface MonthPickerProps {
  value?: Date | null
  placeholder?: string
  className?: string
  disabled?: boolean
  maxDate?: Date | null
  minDate?: Date | null
  scrollableYearDropdown?: boolean
  dateFormat?: string
  onChange: (value: DatePeriod | null) => void
  overrideLastMonthForTesting?: boolean
}

export function MonthPicker({
  value: externalValue,
  placeholder,
  onChange,
  dateFormat,
  overrideLastMonthForTesting,
  className,
  ...props
}: MonthPickerProps) {
  const [value, setValue] = useState<Date | null>(externalValue ?? null)

  const [isLastThirtyDaysSelected, setIsLastThirtyDaysSelected] = useState(
    externalValue === undefined || externalValue === null
  )
  const startRef = useRef<ReactDatePicker>(null)
  const isFirstRender = useRef(true)

  const onMonthChange = (date: Date) => {
    setValue(date)
    setIsLastThirtyDaysSelected(false)
    onChange(getFullMonth(date))
  }

  const onClickLastThirtyDays = () => {
    setValue(null)
    setIsLastThirtyDaysSelected(true)
    onChange(getLastOneMonth())
    startRef?.current?.setOpen(false)
  }

  useEffect(() => {
    if (externalValue !== undefined && externalValue !== null) return
    overrideLastMonthForTesting
      ? onChange({ start: '2022-12-01T03:00:00.000Z', end: '2022-12-31T03:00:00.000Z' })
      : onChange(getLastOneMonth())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    if (externalValue === undefined) return
    if (isLastThirtyDaysSelected && externalValue !== null) return
    setValue(externalValue)
    setIsLastThirtyDaysSelected(externalValue === null)
    if (externalValue === null) {
      onChange(getLastOneMonth())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalValue])

  return (
    <div className='date-month-picker-root'>
      <ReactDatePicker
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={startRef as any}
        selected={value}
        showPopperArrow={false}
        dateFormat={dateFormat ?? 'MMM yyyy'}
        customInput={
          <CustomInputSelect isLastThirtyDaysSelected={isLastThirtyDaysSelected} className={className} />
        }
        calendarClassName='shadow-xl ring-1 ring-black/5 p-6 border-0! rounded-md'
        renderCustomHeader={(props) => (
          <MonthPickerHeader
            onClickLastThirtyDays={onClickLastThirtyDays}
            isLastThirtyDaysSelected={isLastThirtyDaysSelected}
            {...props}
          />
        )}
        placeholderText={placeholder}
        onChange={onMonthChange}
        disabledKeyboardNavigation
        showMonthYearPicker
        {...props}
      />
    </div>
  )
}

export type CustomInputSelectProps = ComponentPropsWithoutRef<'button'> & {
  isLastThirtyDaysSelected?: boolean
}

const CustomInputSelect = forwardRef<HTMLButtonElement, CustomInputSelectProps>(function CustomInputSelect(
  { value, isLastThirtyDaysSelected, className, ...props },
  ref
) {
  const innerRef = useRef<HTMLButtonElement>(null)
  // Just a trick to use ref while also forwarding
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  useImperativeHandle(ref, () => innerRef.current!)

  return (
    <button
      ref={innerRef}
      type='button'
      {...props}
      className={cn(
        'mt-1 flex w-36 cursor-pointer items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 font-inter text-sm text-gray-900 shadow-xs',
        className
      )}
      onClick={() => {
        innerRef.current?.focus()
      }}
      aria-label='open calendar modal'
    >
      <span>{isLastThirtyDaysSelected ? 'Last 30 days' : value}</span>

      <ChevronUpDownIcon className='h-5 w-5 font-bold text-gray-400' aria-hidden='true' />
    </button>
  )
})
